// shared/i18n/useLang.ts
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n, { changeLanguage, Lang, SUPPORTED_LANGS } from "locales/i18n";

const LABELS: Record<Lang, string> = {
	ru: "RU",
	en: "EN",
	tr: "TR",
};

const normalizeLang = (raw?: string): Lang => {
	const base = (raw || "ru").split("-")[0] as Lang;
	return (SUPPORTED_LANGS as readonly string[]).includes(base) ? base : "ru";
};

export const useLang = () => {
	// ✅ this hook subscribes to i18n changes => rerender on language change
	useTranslation();

	const lang = normalizeLang(i18n.language);

	const setLang = useCallback(
		async (next: Lang) => {
			if (next === lang) return;

			// ✅ explicitly persist (even though detector does it)
			localStorage.setItem("app.lang", next);

			await changeLanguage(next);
		},
		[lang],
	);

	const options = useMemo(
		() =>
			(SUPPORTED_LANGS as readonly Lang[]).map((l) => ({
				value: l,
				label: LABELS[l],
			})),
		[],
	);

	const value = useMemo(
		() => options.find((o) => o.value === lang) ?? null,
		[options, lang],
	);

	return { lang, setLang, options, value };
};
